import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './sic-flyout.css.lit';
import {
  removeHangingOpen,
  getCurrentPath,
  _dispatchMenuOpenEvent,
  _dispatchActiveEvent,
} from '../utility';
import { MenuNavItem } from '../types';

/**
 * The SicFlyout component
 * @element sic-flyout
 * @slot sub - for sic-nav-item/sic-nav-link
 */
@customElement('sic-flyout')
export class SicFlyout extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  private _seed = Math.floor(Math.random() * 100000).toString();

  /**
   * Item data
   */
  @property({ type: Object, attribute: 'item' })
  item: MenuNavItem;

  /**
   * Attribute used to determine if the item is a top level parent item.
   */
  @property({ type: Boolean })
  toplevel: boolean;

  /**
   * ID of parent element.
   */
  @property({ type: String, reflect: false, attribute: 'parent-id' })
  parentID: string;

  /**
   * Id used for accessibility purposes.
   */
  @property({ type: String, attribute: 'wrapper-id' })
  wrapperId = `list-flyout-${this._seed}`;

  /**
   * ID of parent element.
   */
  @property({ type: String, reflect: true, attribute: 'id' })
  id: string;

  /**
   * Open state of menu.
   */
  @property({ type: Boolean, reflect: true })
  open = false;

  firstUpdated() {
    this.checkActive();
  }

  render(): TemplateResult {
    const wrapperClasses = {
      'list-flyout': true,
      'open': this.open,
    };
    return html`
      <div
        class="${classMap(wrapperClasses)}"
        id=${this.wrapperId}
        ?engaged=${this.open}
        @activeListener=${this.updateActive}
      >
        ${this.buttonTemplate()} ${this.mainListTemplate(this.item.sub!)}
      </div>
    `;
  }

  /**
   * Checks if this.item.id matches current window path, emits active event if true.
   */
  checkActive() {
    if (this.item.url && getCurrentPath(this.item.url)) {
      this.setAttribute('active', 'active');
      if (this.item.parentID) {
        _dispatchActiveEvent(this);
      }
    }
  }

  /**
   * Updates the active status if activeListener event returns item.id.
   * @param e
   */
  updateActive(e: CustomEvent) {
    const childElementActive = e.detail.parentID === this.item.id;
    if (childElementActive) {
      this.setAttribute('active', 'active');
      _dispatchActiveEvent(this);
    }
  }

  /**
   * Returns the top level menu components and passes any sub arrays to this.listTemplate
   * @param sub Array<MenuNavItem>
   * @returns li wrapped <sic-nav-item>/<sic-nav-link> components.
   */
  mainListTemplate(sub: Array<MenuNavItem>) {
    return html` <ul class="list-flyout--sub-menu" id="${this._seed}-sub-menu">
      ${this.mainLinkTemplate()}
      ${sub.map(item => {
        const subItems = item.sub ? item.sub : false;
        if (subItems) {
          this.listTemplate(
            subItems,
            subItems.length > 7 ? 'columns' : undefined
          );
          return html`<li>
            <sic-nav-item id=${item.id} .item=${item}></sic-nav-item>
          </li>`;
        } else {
          return html`<li>
            <sic-nav-link bold id=${item.id} .item=${item}></sic-nav-link>
          </li>`;
        }
      })}
    </ul>`;
  }

  /**
   * @param sub
   * @param className
   * @returns Returns ul list of sic-nav-link components.
   */
  listTemplate(sub: Array<MenuNavItem>, className?: string): TemplateResult {
    return html` <ul class="${className ? className : null}">
      ${sub.map(
        item => html` <li>
          <sic-nav-link .item=${item} > <sic-nav-link>
        </li>`
      )}
    </ul>`;
  }

  /**
   *
   * @returns Flyout main link.
   */
  mainLinkTemplate() {
    return html`
      <li class="list-flyout--link-main">
        <a
          aria-label=${ifDefined(this.item.aria)}
          href=${ifDefined(this.item.url)}
        >
          ${this.item.text}
          <sic-icon
            size="22px"
            class="list-flyout--button-icon"
            name="chevron-right"
            svg-fill="var(--color-primary-default)"
          ></sic-icon>
        </a>
      </li>
    `;
  }

  /**
   *
   * @returns Flyout label.
   */
  spanTemplate() {
    return html` <span class="list-flyout--label"> ${this.item.text} </span> `;
  }

  buttonTemplate() {
    return html`
      <button
        class="list-flyout--button"
        id="${this._seed}-button"
        aria-controls="${this._seed}-sub-menu"
        aria-label="${this.item.aria ? this.item.aria : this.item.text}"
        aria-expanded="${this.open}"
        aria-haspopup="true"
        @click=${this.handleToggleMenu}
        @keydown=${this.handleToggleMenu}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
      >
        ${this.spanTemplate()}
        <sic-icon
          size="14px"
          class="list-flyout--button-icon"
          name="${this.open ? 'chevron-up' : 'chevron-down'}"
          svg-fill="var(--color-primary-default)"
          aria-hidden="true"
        ></sic-icon>
      </button>
    `;
  }

  /**
   * mouseOver Event handler
   */
  desktopMouseOverOpen() {
    this.applyEngagedAttribute();
    this.open = true;
  }

  /**
   * mouseOut Event handler
   */
  desktopMouseOutClose() {
    this.removeEngagedAttribute();
    this.open = false;
  }

  /**
   * Focus Event handler which applies a focus style to the
   * main wrapper component when the button has focus
   */
  handleFocus() {
    if (this.open) {
      this.shadowRoot
        ?.getElementById(`${this.wrapperId}`)
        ?.classList.add('pseudo-focus');
    }
  }

  /**
   * Blur event that removes focus styles from wrapper whn button loses focus.
   */
  handleBlur() {
    this.shadowRoot
      ?.getElementById(`${this.wrapperId}`)
      ?.classList.remove('pseudo-focus');
  }

  applyEngagedAttribute() {
    this.shadowRoot!.children[0].setAttribute('engaged', 'engaged');
  }

  removeEngagedAttribute() {
    this.shadowRoot!.children[0].removeAttribute('engaged');
  }

  toggleMenu() {
    if (this.open) {
      this.removeEngagedAttribute();
      this.open = !this.open;
    } else {
      this.applyEngagedAttribute();
      this.open = !this.open;
      removeHangingOpen();
    }
  }

  handleToggleMenu(e: KeyboardEvent) {
    const keyed = e.type === 'keydown';
    if (keyed) {
      // make sure menu toggles only on Space and Enter keys
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleMenu();
        _dispatchMenuOpenEvent(this);
      }
    } else {
      this.toggleMenu();
      _dispatchMenuOpenEvent(this);
    }
  }
}
